import { Alert, Button, Drawer, Group, Modal, Skeleton, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconDownload,
  IconListTree,
  IconPrinter,
  IconRefresh,
  IconX,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { useShell } from '../../shell-context.js';
import { DocumentRecords } from '../forms/DocumentRecords.js';
import { useLibraryFormActions } from '../forms/LibraryFormActions.js';
import { printPdfUrl } from '../forms/print.js';
import type { FormRecordSource } from '../forms/source.js';
import { formErrorText } from '../forms/status.js';
import { errorCode } from '../personnel/source.js';
import { ApprovalChainPanel } from './ApprovalChainEditor.js';
import { type DetailsTab, DocumentDetailsPanel } from './DocumentDetailsPanel.js';
import { DocumentHeader } from './DocumentHeader.js';
import { DocumentSidebar } from './DocumentSidebar.js';
import classes from './Documents.module.css';
import { DocumentTree } from './DocumentTree.js';
import { FileViewer, type FileViewerHandle } from './FileViewer.js';
import { FOLDER_SCREEN_PATH } from './folder-tree.js';
import {
  buildTree,
  canOutputRevision,
  changesRevision,
  createRequestGate,
  dayRolloverTick,
  documentHeading,
  firstDocumentId,
  isUpdated,
  libraryLoading,
  mainDownloadName,
  missingRevisionFile,
  observedState,
  outputBlock,
  previewRevision,
  printFooter,
  revisionBare,
  type ServedObservation,
  sectionHref,
  strictestState,
  vesselDay,
  vesselOffsetMinutes,
} from './library.js';
import { OfficeHeaderActions, RevisionActionDialog, RevisionWorkflow } from './OfficeParts.js';
import { openRevision } from './office.js';
import { RevisionDrawer, type RevisionFormMode } from './RevisionDrawer.js';
import {
  AcknowledgeDialog,
  ChangesStrip,
  NotOnBoardCard,
  queueItemFor,
  ReadDialog,
  ReadStrip,
} from './ShipParts.js';
import type { DocumentPermissions, DocumentSource, RevisionAction } from './source.js';
import { useLibraryTreeRecords } from './TreeRecords.js';
import type {
  DownloadsView,
  LibraryDetail,
  LibraryDocument,
  LibraryRevision,
  LibrarySection,
  LibraryTree,
  ShipUploadsView,
} from './types.js';
import {
  NotUploadedCard,
  ReadOnlyStrip,
  ShipUploadMarkerBadge,
  UploadStatusBadge,
  UploadSummary,
} from './VesselLibraryParts.js';
import {
  COMPANY_DOCUMENTS,
  canCreateDocument,
  canEditDocument,
  inRetiredFolder,
  shipUploadMarker,
  unifiedLibrary,
} from './vessel-library.js';

export interface DocumentLibraryScreenProps {
  section: LibrarySection;
  source: DocumentSource;
  /** Ekranın üstündeki eylemler (web ofiste "Distribution", "New Document"; Task 17). */
  actions?: ReactNode;
  /** Edit: yeni revizyon çekmecesi (yalnız web ofis; Task 17). */
  onEdit?: (detail: LibraryDetail) => void;
  /**
   * Başka bir sayfanın sekmesinde (PMS Instructions, Maintenance Plan; K38): sayfa başlığı
   * çizilmez, eylemler araç çubuğundadır; dağıtım panosu yoktur.
   */
  embedded?: boolean;
  /**
   * ISM form kayıtları (M8b3; ADR 0042 §18–§21): gemide Form List'in Actions'ında Open Form,
   * Print Form (kayıt açar), Fill in app ve New record. Verilmezse Open Form pasif kalır.
   */
  forms?: FormRecordSource | undefined;
  /**
   * Web ofiste kütüphane seçici (breadcrumb satırı; M8a4e): "Company documents" ve her geminin
   * kütüphanesi. Seçim adreste `?library=` olarak kalır.
   */
  librarySelector?: ReactNode;
}

const NO_PERMISSIONS: DocumentPermissions = {
  create: false,
  edit: false,
  approve: false,
  export: false,
  acknowledge: false,
  manageDownloads: false,
};

type TreeState =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; tree: LibraryTree };
type DetailState =
  | { kind: 'idle' }
  | { kind: 'loading'; id: string }
  | { kind: 'error'; id: string; code: string }
  | { kind: 'ready'; detail: LibraryDetail };

/**
 * ISM Forms ▸ Form List ve Manuals (gemi-ism-formlari.jpeg; plan M8a Task 14, 18): başlık kartı,
 * Form Tree, Form Details (sekmeler, bilgi şeridi, önizleyici) ve sağ sütun. Gemi uygulaması ve
 * web ofis aynı ekranı kendi kaynağıyla kullanır (`DocumentSource`). Seçim adrese yazılır
 * (`?doc=`, önizlenen eski revizyon `?rev=`).
 */
export function DocumentLibraryScreen({
  section,
  source,
  actions,
  onEdit,
  embedded = false,
  forms,
  librarySelector,
}: DocumentLibraryScreenProps) {
  const { t } = useTranslation();
  const shell = useShell();
  const labels = (key: string) => t(`documents.sections.${section}.${key}`);
  const params = useMemo(() => new URLSearchParams(shell?.search ?? ''), [shell?.search]);
  const docParam = params.get('doc');
  const revParam = params.get('rev');
  const libraryParam = params.get('library');
  const [permissions, setPermissions] = useState<DocumentPermissions>(NO_PERMISSIONS);
  const [treeState, setTreeState] = useState<TreeState>({ kind: 'loading' });
  const [detailState, setDetailState] = useState<DetailState>({ kind: 'idle' });
  const [tab, setTab] = useState<DetailsTab>('view');
  const [fallbackId, setFallbackId] = useState<string | null>(null);
  const [confirmDownload, setConfirmDownload] = useState<{ fileId: string; name: string } | null>(
    null,
  );
  const [notice, setNotice] = useState<{ tone: 'danger' | 'success'; text: string } | null>(null);
  const [treeOpen, setTreeOpen] = useState(false);
  const [printing, setPrinting] = useState(false);
  const viewer = useRef<FileViewerHandle | null>(null);
  const narrow = useMediaQuery('(max-width: 1023px)', false);
  const [downloads, setDownloads] = useState<DownloadsView | null>(null);
  const [requested, setRequested] = useState<Set<string>>(new Set());
  // Teyit ve okuma penceresinin hedefi açılırken yakalanır: pencere açıkken detay yeniden okunsa
  // da kişi gördüğü revizyonu imzalar (mimari belge §7.3; inceleme M8a3-R6). Pencere kapanınca
  // bağlı kalır (S5): son hedefle çizilir.
  type AckTarget = { kind: 'adopted' | 'withdrawn'; revision: LibraryRevision };
  const [ackTarget, setAckTarget] = useState<AckTarget | null>(null);
  const [lastAck, setLastAck] = useState<AckTarget | null>(null);
  const [readTarget, setReadTarget] = useState<LibraryRevision | null>(null);
  const [lastRead, setLastRead] = useState<LibraryRevision | null>(null);
  const [now, setNow] = useState(() => new Date().toISOString());
  // Önizleyicinin dosya ucundan okuduğu durum (`x-felix-revision-state`; inceleme WF-1)
  const [served, setServed] = useState<ServedObservation | null>(null);
  // Web ofiste yönetim ve onay akışı (Task 17); gemide geminin kendi belgeleri (M8a4e)
  const office = source.scope === 'web' ? source.office : undefined;
  const shipLibrary = source.scope === 'ship' ? source.shipLibrary : undefined;
  const [shipUploads, setShipUploads] = useState<ShipUploadsView | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending'>('all');
  const [drawer, setDrawer] = useState<RevisionFormMode | null>(null);
  // "Remove document" onayı (ADR 0142 §5)
  const [removing, setRemoving] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    action: RevisionAction;
    revision: LibraryRevision;
  } | null>(null);
  const [lastAction, setLastAction] = useState<typeof pendingAction>(null);
  useEffect(() => {
    if (pendingAction) setLastAction(pendingAction);
  }, [pendingAction]);

  const selectedId = docParam ?? fallbackId;
  // Geç dönen eski yanıt yeni seçimi ya da yeni ağacı ezmez (inceleme M8a3-R8)
  const treeGate = useRef(createRequestGate());
  const detailGate = useRef(createRequestGate());

  const loadTree = useCallback(async () => {
    const ticket = treeGate.current.next();
    try {
      const tree = await source.tree(section);
      if (!treeGate.current.isLatest(ticket)) return;
      setTreeState({ kind: 'ready', tree });
      setNow(new Date().toISOString());
    } catch (error) {
      if (!treeGate.current.isLatest(ticket)) return;
      setTreeState((current) =>
        current.kind === 'ready' ? current : { kind: 'error', code: errorCode(error) },
      );
    }
  }, [source, section]);

  const loadDetail = useCallback(
    async (id: string) => {
      const ticket = detailGate.current.next();
      setDetailState((current) =>
        current.kind === 'ready' && current.detail.id === id ? current : { kind: 'loading', id },
      );
      try {
        const detail = await source.detail(id);
        if (!detailGate.current.isLatest(ticket)) return;
        setDetailState({ kind: 'ready', detail });
      } catch (error) {
        if (!detailGate.current.isLatest(ticket)) return;
        setDetailState({ kind: 'error', id, code: errorCode(error) });
      }
    },
    [source],
  );

  useEffect(() => {
    setTreeState({ kind: 'loading' });
    void loadTree();
    void source.permissions().then(setPermissions, () => setPermissions(NO_PERMISSIONS));
  }, [loadTree, source]);

  // Adreste seçim yoksa ağaçtaki ilk doküman açılır (tasarımda Form 01 seçili)
  useEffect(() => {
    if (docParam || treeState.kind !== 'ready') return;
    setFallbackId((current) => {
      if (current && treeState.tree.documents.some((d) => d.id === current)) return current;
      return firstDocumentId(buildTree(treeState.tree));
    });
  }, [docParam, treeState]);

  useEffect(() => {
    if (selectedId) void loadDetail(selectedId);
    else setDetailState({ kind: 'idle' });
  }, [selectedId, loadDetail]);

  // Gemide dosya doğrulandı, teyit verildi ya da senkron turu bitti: ağaç ve detay yeniden okunur
  // (ADR 0028 #39; inceleme WF-1)
  useEffect(() => {
    if (!source.subscribe) return;
    return source.subscribe(() => {
      void loadTree();
      if (selectedId) void loadDetail(selectedId);
    });
  }, [source, loadTree, loadDetail, selectedId]);

  // Gemi yerel günü dönünce ileri tarihli revizyon yürürlüğe girer, eskisi kalkar (ADR 0028 #27):
  // ekran olay beklemeden yeniden okur (inceleme WF-1)
  useEffect(() => {
    if (source.scope !== 'ship') return;
    const zone = source.printer.timeZone;
    let rollover = { day: vesselDay(zone, new Date()), graceLeft: 0 };
    const timer = setInterval(() => {
      // Gün değişince ve sonraki beş dakika: sunucu saati PC'den geride olabilir (M8a3-R16)
      const tick = dayRolloverTick(rollover, vesselDay(zone, new Date()));
      rollover = tick.state;
      if (!tick.reload) return;
      void loadTree();
      if (selectedId) void loadDetail(selectedId);
    }, 60_000);
    return () => clearInterval(timer);
  }, [source, loadTree, loadDetail, selectedId]);

  // Seçili kütüphane adreste kalır (web ofis kütüphane seçici)
  const go = (doc: string | null, rev: string | null = null) => {
    if (!shell) return;
    shell.navigate(sectionHref(section, { library: libraryParam, doc, rev }));
  };

  // Gemi detayı ağaçtaki rozet alanlarıyla tamamlanır ("Updated", "Withdrawn"; ADR 0028 #39)
  const summary =
    detailState.kind === 'ready' && treeState.kind === 'ready'
      ? treeState.tree.documents.find((d) => d.id === detailState.detail.id)
      : undefined;
  const detail: LibraryDetail | null =
    detailState.kind === 'ready'
      ? {
          ...detailState.detail,
          ...(summary?.updatedUntil !== undefined ? { updatedUntil: summary.updatedUntil } : {}),
          ...(summary?.withdrawn !== undefined ? { withdrawn: summary.withdrawn } : {}),
        }
      : null;
  const revision: LibraryRevision | null = detail ? previewRevision(detail, revParam) : null;
  const treeData = treeState.kind === 'ready' ? treeState.tree : null;
  // Ofiste bir geminin kütüphanesi salt okunurdur (ADR 0038 §1); gemide ve orada şirket ve gemi
  // belgeleri birlikte durur, şirket belgesi "Company" rozetli (§3)
  const readOnly = treeData?.readOnly === true;
  const unified = unifiedLibrary(source.scope, treeData);
  const vesselDetail = detail?.owner === 'vessel';
  const writeContext = {
    scope: source.scope,
    office: Boolean(office),
    shipLibrary: Boolean(shipLibrary),
    readOnly,
  };
  // Onay akışını yazan: ofiste şirket belgesi, gemide geminin kendi belgesi
  const workflow =
    office && !vesselDetail ? office : shipLibrary && vesselDetail ? shipLibrary : null;
  // Ofiste gemi belgesinin dosyası buluta henüz ulaşmadıysa açılmaz (`FILE_NOT_UPLOADED`; §14)
  const upload = readOnly && vesselDetail && revision ? revision.uploads?.[revision.file.id] : null;
  const notUploaded =
    readOnly && vesselDetail && revision !== null && upload?.state !== 'available';
  // Gemide dosya doğrulanmadan önizlenmez: "henüz gemide değil" kartı (plan Task 16); kart eksik
  // olan dosyayı (ana dosya ya da baskı PDF'i) gösterir (inceleme F2)
  const missing =
    source.scope === 'ship' && revision !== null && revision.onBoard === false
      ? (missingRevisionFile(revision) ?? { fileId: revision.file.id, size: revision.file.size })
      : null;
  const scope = source.scope;
  // Gözlem yalnız gözlendiği detayla geçerli: gün dönünce eski "scheduled" kalmaz (M8a3-R14)
  const servedState = revision ? observedState(revision, served) : null;
  // Detay ile dosya ucunun durumundan sıkı olanı: gemide yalnız yürürlükteki (ADR 0032 #87);
  // gemide dosya henüz doğrulanmadıysa önizleyici ve basılacak sayfa yok (inceleme M8a3-R12)
  const block = outputBlock(scope, revision?.state ?? null, servedState, missing !== null);
  const canOutput = permissions.export && block === null && !notUploaded;
  // Pasif çıktının nedeni ipucunda ve ekran okuyucuda (inceleme M8a3-R13)
  const outputHint = block
    ? t(`documents.viewer.blocked.${block}`)
    : notUploaded
      ? t('documents.errors.FILE_NOT_UPLOADED')
      : null;
  const outputHintId = useId();
  // İskelet yalnız veri beklenirken; boş kütüphanede ve hatada değil (inceleme M8a3-R5)
  const loading = libraryLoading(
    treeState.kind,
    treeState.kind === 'ready' ? treeState.tree.documents.length : 0,
    detailState.kind,
  );
  const offsetMin = source.printer.timeZone
    ? vesselOffsetMinutes(source.printer.timeZone, now)
    : undefined;

  // Uyarı tam ekranın altında kalmasın: görünürken tam ekrandan çıkılır (inceleme S3)
  useEffect(() => {
    if (notice) viewer.current?.exitFullscreen();
  }, [notice]);

  const fail = (error: unknown) =>
    setNotice({
      tone: 'danger',
      text: t(`documents.errors.${errorCode(error)}`, {
        defaultValue: t('documents.errors.GENERIC'),
      }),
    });

  // Kartın kuyruk bilgisi: dosya gemide değilse indirme kuyruğu okunur; kuyruk olayında ve
  // "Download now"dan sonra yeniden (inceleme WF-11, F7)
  const missingFile = missing?.fileId ?? null;
  const [queueTick, setQueueTick] = useState(0);
  useEffect(() => {
    if (!missingFile || !source.ship) {
      setDownloads(null);
      return;
    }
    void queueTick;
    let live = true;
    void source.ship.downloads().then(
      (view) => live && setDownloads(view),
      () => live && setDownloads(null),
    );
    return () => {
      live = false;
    };
  }, [missingFile, source, queueTick]);
  useEffect(() => {
    if (!missingFile || !source.subscribe) return;
    return source.subscribe(() => setQueueTick((value) => value + 1));
  }, [missingFile, source]);

  // Gemide gemi belgesinin dosyalarının buluta yükleme durumu (History işareti; ADR 0038 §14):
  // belge açılınca ve kuyruk ya da doküman olayında yeniden okunur
  const detailId = detail?.id ?? null;
  const [uploadsTick, setUploadsTick] = useState(0);
  useEffect(() => {
    if (!shipLibrary || !vesselDetail || !detailId) {
      setShipUploads(null);
      return;
    }
    void uploadsTick;
    let live = true;
    void shipLibrary.uploads().then(
      (view) => live && setShipUploads(view),
      () => live && setShipUploads(null),
    );
    return () => {
      live = false;
    };
  }, [shipLibrary, vesselDetail, detailId, uploadsTick]);
  useEffect(() => {
    if (!shipLibrary || !vesselDetail || !source.subscribe) return;
    return source.subscribe(() => setUploadsTick((value) => value + 1));
  }, [shipLibrary, vesselDetail, source]);

  const requestDownload = async (fileId: string) => {
    try {
      await source.ship?.requestDownload(fileId);
      setRequested((current) => new Set(current).add(fileId));
      setQueueTick((value) => value + 1);
    } catch (error) {
      fail(error);
    }
  };

  const startAcknowledge = (kind: 'adopted' | 'withdrawn') => {
    const target = detail ? changesRevision(detail, revision) : null;
    if (!target) return;
    setAckTarget({ kind, revision: target });
    setLastAck({ kind, revision: target });
  };

  const acknowledge = async (password: string) => {
    if (!detail || !ackTarget || !source.ship) return;
    const target = ackTarget.revision;
    await source.ship.acknowledge(target.id, {
      kind: ackTarget.kind,
      contentHash: target.contentHash,
      // Parola isteğe bağlı (ADR 0056): yalnız girildiyse
      ...(password ? { password } : {}),
    });
    setAckTarget(null);
    setNotice({ tone: 'success', text: t('documents.acknowledge.done') });
    await loadDetail(detail.id);
    void loadTree();
  };

  const confirmRead = async (password: string) => {
    if (!detail || !readTarget || !source.ship) return;
    // Gemi belgesinin okuma beyanı kendi tablosuna (ADR 0038 §12)
    const secret = password || undefined;
    if (vesselDetail && shipLibrary) await shipLibrary.confirmRead(readTarget.id, secret);
    else await source.ship.confirmRead(readTarget.id, secret);
    setReadTarget(null);
    setNotice({ tone: 'success', text: t('documents.read.done') });
    await loadDetail(detail.id);
  };

  const download = (file: { fileId: string; name: string }, state = revision?.state) => {
    // Ofiste yürürlükte olmayan revizyon yalnız başvuru içindir: önce sorulur (#30 E)
    if (scope === 'web' && state && state !== 'published') {
      setConfirmDownload(file);
      return;
    }
    void source.downloadFile(file).catch(fail);
  };

  // Önerilen ad revizyonu taşır: "Form 01 - … (Rev 1.0).docx" (ADR 0028 #30 C; inceleme F3)
  const downloadMain = () => {
    if (!detail || !revision) return;
    const state = strictestState(revision.state, servedState);
    download({ fileId: revision.file.id, name: mainDownloadName(detail, { ...revision, state }) });
  };

  /**
   * Basılı kopya (ADR 0028 #30). Print Form'da (ADR 0042 §20) altlığa kayıt no eklenir ve
   * revizyonun onaylı baskı PDF'i varsa o basılır; başlıktaki Print boş kopyadır.
   */
  const print = async (form?: { recordNo: string; renditionUrl: string | null }) => {
    if (!detail || !revision) return;
    // Baskı PDF'i varsa başlıktaki Print de onu basar (ADR 0028 #34(3); ADR 0055 §5)
    const renditionOf = (r: LibraryRevision) => {
      const file = r.attachments.find((a) => a.role === 'print_rendition');
      return file ? source.fileUrl(file.id, 'preview') : null;
    };
    // Önizleyici yoksa sessiz kalınmaz (inceleme M8a3-R12)
    if (!viewer.current && !form?.renditionUrl && !renditionOf(revision)) {
      const code = missing ? 'FILE_NOT_ON_BOARD' : 'PREVIEW_NOT_READY';
      fail(Object.assign(new Error(code), { code }));
      return;
    }
    setPrinting(true);
    try {
      let base = detail;
      let target = revision;
      if (scope === 'ship') {
        // Gemide basmadan hemen önce yeniden okunur: senkron turu revizyonu yürürlükten kaldırmış
        // olabilir; basılı kopya yalnız yürürlükteki revizyondan (ADR 0032 #87; inceleme WF-1)
        // [ISM 11.2.3]
        const fresh = await source.detail(detail.id);
        // Bu okuma en tazesi: daha önce başlamış okumalar onu ezmez (M8a3-R8)
        detailGate.current.next();
        setDetailState({ kind: 'ready', detail: fresh });
        const again = fresh.revisions.find((r) => r.id === revision.id);
        if (!again || !canOutputRevision('ship', strictestState(again.state, servedState))) {
          throw Object.assign(new Error('REVISION_NOT_CURRENT'), { code: 'REVISION_NOT_CURRENT' });
        }
        base = { ...fresh, withdrawn: detail.withdrawn ?? null };
        target = again;
      }
      const current = base.revisions.find((r) => r.id === base.current?.revisionId);
      const origin = base.revisions.find((r) => r.id === target.reinstatedFromRevisionId);
      const footer = printFooter((key, options) => t(key, options ?? {}), {
        kind: base.kind,
        docNo: base.docNo,
        revisionNo: target.revisionNo,
        effectiveOn: target.effectiveOn ?? target.publishedAt?.slice(0, 10) ?? null,
        printedAt: new Date().toISOString(),
        offsetMin: offsetMin ?? null,
        printedBy: source.printer.name,
        place: source.printer.place,
        contentHash: target.contentHash,
        state: strictestState(target.state, servedState),
        currentRevisionNo: current?.revisionNo ?? base.current?.revisionNo ?? null,
        withdrawnOn: base.withdrawn?.at.slice(0, 10) ?? null,
        reinstatedFromRevisionNo: origin?.revisionNo ?? null,
        recordNo: form?.recordNo ?? null,
      });
      const renditionUrl = form ? form.renditionUrl : renditionOf(target);
      if (renditionUrl) {
        await printPdfUrl(renditionUrl, footer, () => source.print());
        return;
      }
      const handle = viewer.current;
      if (!handle)
        throw Object.assign(new Error('PREVIEW_NOT_READY'), { code: 'PREVIEW_NOT_READY' });
      const cleanup = await handle.preparePrint(footer);
      try {
        await source.print();
      } finally {
        cleanup();
      }
    } catch (error) {
      fail(error);
    } finally {
      setPrinting(false);
    }
  };

  const toggleFavorite = async () => {
    if (!detail) return;
    try {
      if (vesselDetail && shipLibrary) await shipLibrary.setFavorite(detail.id, !detail.favorite);
      else await source.setFavorite(detail.id, !detail.favorite);
      setDetailState({ kind: 'ready', detail: { ...detail, favorite: !detail.favorite } });
    } catch (error) {
      fail(error);
    }
  };

  // ISM form kaydı eylemleri (gemide; ADR 0042 §18–§21)
  const formActions = useLibraryFormActions({
    forms,
    documents: source,
    section,
    detail,
    permissions,
    print: (options) => print(options),
    onError: (error) => setNotice({ tone: 'danger', text: formErrorText(t, error) }),
  });

  /** Onay akışı işlemi: içerik hash'iyle; sonra detay ve ağaç yeniden okunur (§7.3). */
  const act = async (
    action: RevisionAction,
    input: { reason?: string; password?: string; effectiveOn?: string | null },
  ) => {
    if (!workflow || !detail || !pendingAction) return;
    const target = pendingAction.revision;
    try {
      await workflow.act(target.id, action, { contentHash: target.contentHash, ...input });
    } catch (error) {
      // Revizyon bu arada değişmiş olabilir (başka onaylayan, içerik): ekran tazelenir, hata
      // pencerede kalır (inceleme WF-7)
      void loadDetail(detail.id);
      void loadTree();
      throw error;
    }
    setPendingAction(null);
    setNotice({
      tone: 'success',
      text: t(`documents.workflow.done.${action}`, {
        revision: revisionBare(target.revisionNo),
      }),
    });
    if (revParam) go(detail.id);
    await loadDetail(detail.id);
    void loadTree();
  };

  const downloadRelated = (related: LibraryDocument) => {
    if (related.current) {
      const name = mainDownloadName(related, {
        revisionNo: related.current.revisionNo,
        state: 'published',
        file: related.current.file,
      });
      download({ fileId: related.current.file.id, name }, 'published');
    }
  };

  const tree = treeState.kind === 'ready' ? treeState.tree : null;
  // Formun altında geminin kayıtları (ADR 0072): gemide geminin, ofiste seçilen geminin
  const treeRecords = useLibraryTreeRecords({ section, forms, library: libraryParam });
  const treePanel = (
    <DocumentTree
      section={section}
      tree={tree}
      selectedId={selectedId}
      now={now}
      onSelect={(id) => {
        setTreeOpen(false);
        setTab('view');
        // Aynı doküman yeniden seçilince de okunur: adres değişmez (inceleme WF-7)
        if (id === selectedId && !revParam) void loadDetail(id);
        go(id);
      }}
      onClearSelection={() => undefined}
      filter={office ? filter : undefined}
      onFilter={office ? setFilter : undefined}
      hideTitle={narrow}
      unified={unified}
      records={treeRecords}
    />
  );
  const open = workflow && detail ? openRevision(detail) : null;
  // İşlem penceresi bağlı kalır (kapalıyken son revizyonla): odak açan düğmeye döner (S5)
  const actionRevision = pendingAction?.revision ?? lastAction?.revision ?? open ?? revision;
  // Kişisel okuma beyanı (ADR 0028 #29): yürürlükteki revizyon, okuma gerekli, kişi kitlede ve
  // henüz beyan etmemiş
  const readDue =
    source.ship &&
    detail &&
    revision &&
    revision.id === detail.current?.revisionId &&
    revision.readRequired &&
    !revision.readByMe &&
    detail.reads?.pending.some((p) => p.userId === source.ship?.userId)
      ? revision
      : null;

  let main: ReactNode;
  if (treeState.kind === 'error') {
    main = (
      <Panel className={classes.details}>
        <EmptyState
          tone="danger"
          title={t('documents.screen.error')}
          description={t(`documents.errors.${treeState.code}`, {
            defaultValue: t('documents.errors.GENERIC'),
          })}
          action={
            <Button
              size="xs"
              variant="light"
              leftSection={<IconRefresh size={14} />}
              onClick={() => void loadTree()}
            >
              {t('documents.screen.retry')}
            </Button>
          }
        />
      </Panel>
    );
  } else if (detailState.kind === 'error') {
    main = (
      <Panel className={classes.details}>
        <EmptyState
          tone="danger"
          title={t('documents.screen.detailError')}
          description={t(`documents.errors.${detailState.code}`, {
            defaultValue: t('documents.errors.GENERIC'),
          })}
          action={
            <Button
              size="xs"
              variant="light"
              leftSection={<IconRefresh size={14} />}
              onClick={() => void loadDetail(detailState.id)}
            >
              {t('documents.screen.retry')}
            </Button>
          }
        />
      </Panel>
    );
  } else if (detail) {
    main = (
      <DocumentDetailsPanel
        section={section}
        scope={scope}
        detail={detail}
        revision={revision}
        tab={tab}
        onTab={setTab}
        onPreviewRevision={(id) => go(detail.id, id === detail.current?.revisionId ? null : id)}
        onDownloadFile={(file) => download(file)}
        canOutput={canOutput}
        offsetMin={offsetMin}
        historyFooter={
          // Formun kayıtları (ADR 0042 §21; gereksinim §9): gemide ve ofiste
          forms && detail.kind === 'form' ? (
            <DocumentRecords source={forms} documentId={detail.id} />
          ) : null
        }
        historyMarker={
          shipLibrary && vesselDetail
            ? (row) => {
                const marker = shipUploadMarker(shipUploads, row.file.sha256);
                return marker ? <ShipUploadMarkerBadge marker={marker} /> : null;
              }
            : readOnly && vesselDetail
              ? (row) => <UploadStatusBadge upload={row.uploads?.[row.file.id]} />
              : undefined
        }
        fileStatus={
          readOnly && vesselDetail && revision
            ? (fileId) => ({
                available: revision.uploads?.[fileId]?.state === 'available',
                note: <UploadStatusBadge upload={revision.uploads?.[fileId]} />,
              })
            : undefined
        }
        historyAction={
          // Geri çekme `approve` + `void` ister (sunucunun eşlemesi; inceleme M8a3-R18)
          workflow && permissions.approve && permissions.void
            ? (row) =>
                row.state === 'published' && row.id === detail.current?.revisionId ? (
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    color="red"
                    leftSection={<IconX size={12} />}
                    onClick={() => setPendingAction({ action: 'withdraw', revision: row })}
                  >
                    {t('documents.workflow.actions.withdraw')}
                  </Button>
                ) : null
            : undefined
        }
        notice={
          open ? (
            <RevisionWorkflow
              detail={detail}
              revision={open}
              permissions={permissions}
              onAction={(action) => setPendingAction({ action, revision: open })}
              previewing={revision?.id === open.id}
              onPreview={() => go(detail.id, open.id)}
              library={vesselDetail ? 'vessel' : 'company'}
            />
          ) : readOnly ? (
            // Ofiste gemi görünümü: revizyon şirket dokümanlarında girilir (ADR 0081)
            <ReadOnlyStrip
              draft={vesselDetail && detail.draftOnVessel === true}
              vessel={vesselDetail}
              place="office"
              onOpenCompany={() =>
                shell?.navigate(sectionHref(section, { library: COMPANY_DOCUMENTS }))
              }
            />
          ) : (
            <>
              {/* Geminin eski belgesi gemide: revize edilemez, şirket yayınlar (ADR 0081) */}
              {scope === 'ship' && vesselDetail ? (
                <ReadOnlyStrip draft={false} vessel place="ship" />
              ) : null}
              {scope === 'ship' &&
              revision &&
              (detail.acknowledgementDue ||
                isUpdated(detail, now) ||
                detail.scheduled ||
                (detail.withdrawn && !detail.current)) ? (
                <ChangesStrip
                  detail={detail}
                  revision={changesRevision(detail, revision) ?? revision}
                  canAcknowledge={permissions.acknowledge}
                  onAcknowledge={startAcknowledge}
                  offsetMin={offsetMin}
                />
              ) : null}
              {readDue ? (
                <ReadStrip
                  revision={readDue}
                  onConfirm={() => {
                    setReadTarget(readDue);
                    setLastRead(readDue);
                  }}
                />
              ) : null}
            </>
          )
        }
        viewer={
          notUploaded && revision ? (
            <div className={classes.viewer} data-status="not-uploaded">
              <div className={classes.viewerBody}>
                <NotUploadedCard upload={upload} size={revision.file.size} />
              </div>
            </div>
          ) : missing ? (
            <div className={classes.viewer} data-status="not-on-board">
              <div className={classes.viewerBody}>
                <NotOnBoardCard
                  item={queueItemFor(downloads, missing.fileId)}
                  active={downloads?.active ?? null}
                  size={missing.size}
                  canRequest={permissions.manageDownloads && Boolean(source.ship)}
                  requested={requested.has(missing.fileId)}
                  onRequest={() => void requestDownload(missing.fileId)}
                />
              </div>
            </div>
          ) : revision ? (
            <FileViewer
              ref={viewer}
              key={revision.id}
              file={{
                id: revision.file.id,
                name: revision.file.name,
                size: revision.file.size,
                kind: revision.fileKind,
              }}
              url={source.fileUrl(revision.file.id, 'preview')}
              state={revision.state}
              canDownload={canOutput}
              canPrint={canOutput}
              onDownload={downloadMain}
              onPrint={() => void print()}
              onServedState={(state) =>
                setServed({ revisionId: revision.id, state, detailState: revision.state })
              }
            />
          ) : (
            <EmptyState title={t('documents.screen.noRevision')} />
          )
        }
      />
    );
  } else if (treeState.kind === 'ready' && treeState.tree.documents.length === 0) {
    main = (
      <Panel title={labels('details')} className={classes.details}>
        <EmptyState
          title={labels('empty')}
          // Ofiste ilk durumda doküman ekleme ve revizyonun yolu (ADR 0081)
          {...(office ? { description: t('documents.screen.officeEmpty') } : {})}
        />
      </Panel>
    );
  } else {
    main = (
      <Panel title={labels('details')} className={classes.details}>
        <div className={classes.detailsSkeleton} aria-busy="true">
          <span className={classes.srOnly}>{t('documents.screen.loading')}</span>
          <Skeleton height={34} width="60%" />
          <Skeleton height={72} mt={14} />
          <Skeleton height={420} mt={14} />
        </div>
      </Panel>
    );
  }

  const headerActions = (
    <>
      {narrow ? (
        <Button
          variant="default"
          size="xs"
          leftSection={<IconListTree size={15} />}
          onClick={() => setTreeOpen(true)}
        >
          {t('documents.screen.showTree')}
        </Button>
      ) : null}
      {librarySelector}
      {actions}
      {shipLibrary && canCreateDocument(writeContext, treeData, permissions) ? (
        <OfficeHeaderActions
          permissions={permissions}
          onNew={() => setDrawer('new')}
          // ISM'de "New Document" doldurma değildir (ortağın geri bildirimi 2026-10-16)
          newHint={section === 'ism' ? t('documents.head.newDocumentFormHint') : undefined}
        />
      ) : null}
      {office ? (
        <OfficeHeaderActions
          permissions={permissions}
          onNew={() => setDrawer('new')}
          onManageFolders={
            section === 'manuals' && office.folders
              ? () => shell?.navigate(FOLDER_SCREEN_PATH.manuals)
              : undefined
          }
          {...(section === 'pms_instructions'
            ? {}
            : {
                onDistribution: () => shell?.navigate(`${sectionHref(section)}/distribution`),
              })}
        />
      ) : null}
    </>
  );

  return (
    <div className={classes.screen} data-section={section} data-scope={scope}>
      {embedded ? (
        <div className={classes.embeddedBar}>{headerActions}</div>
      ) : (
        <PageHeader
          title={labels('title')}
          titleHidden
          {...(detail ? { crumb: documentHeading(detail) } : {})}
          actions={headerActions}
        />
      )}
      {notice ? (
        <Alert
          color={notice.tone === 'danger' ? 'red' : 'green'}
          icon={
            notice.tone === 'danger' ? (
              <IconAlertTriangle size={18} />
            ) : (
              <IconCircleCheck size={18} />
            )
          }
          withCloseButton
          closeButtonLabel={t('documents.screen.dismiss')}
          onClose={() => setNotice(null)}
          className={classes.notice}
        >
          {notice.text}
        </Alert>
      ) : null}

      {readOnly ? <UploadSummary summary={treeData?.uploads ?? null} /> : null}

      <div className={classes.layout}>
        <div className={classes.headArea}>
          <DocumentHeader
            section={section}
            scope={scope}
            detail={detail}
            permissions={permissions}
            canOutput={canOutput}
            outputHint={outputHint}
            loading={loading}
            canEdit={detail ? canEditDocument(writeContext, detail, permissions) : false}
            // Onaylayan (DPA) revizyonu hazırlamaz: düğme pasif, kimin hazırladığı yazılı (ADR 0081)
            revisionHint={
              office && !vesselDetail && !permissions.create && permissions.approve
                ? t('documents.head.revisionHint')
                : null
            }
            editBlocked={
              detail && vesselDetail && inRetiredFolder(detail, treeData?.folders ?? [])
                ? 'retired'
                : null
            }
            unified={unified}
            onEdit={() => {
              if (!detail) return;
              if (office || shipLibrary) setDrawer('revision');
              else onEdit?.(detail);
            }}
            // Yanlış yüklenen şirket dokümanı (ADR 0142 §5); kuralları sunucu denetler
            onRemove={
              office?.removeDocument &&
              detail &&
              !vesselDetail &&
              canEditDocument(writeContext, detail, permissions)
                ? () => setRemoving(true)
                : undefined
            }
            onDownload={downloadMain}
            onPrint={() => void print()}
            primaryAction={formActions?.primary}
            replacesPrint={formActions?.replacesPrint}
          />
        </div>
        {narrow ? (
          <Drawer
            opened={treeOpen}
            onClose={() => setTreeOpen(false)}
            title={labels('tree')}
            size={340}
            classNames={{ body: classes.drawerBody }}
          >
            {treePanel}
          </Drawer>
        ) : (
          <div className={classes.treeArea}>{treePanel}</div>
        )}
        <div className={classes.mainArea}>{main}</div>
        {/* Sağ sütun kendi içinde kayar: salt okunur gemi belgesinde içinde odaklanılacak düğme
            kalmayabilir; klavyeyle kaydırılabilsin diye sütun odak alır (axe
            scrollable-region-focusable; M8a4e) */}
        {/* biome-ignore lint/a11y/noNoninteractiveTabindex: kayan bölge klavyeyle kaydırılır */}
        <aside className={classes.sideArea} aria-label={labels('information')} tabIndex={0}>
          {detail && office && !vesselDetail && detail.kind === 'form' ? (
            // Formun onay zinciri (ADR 0071 §2): sağ sütunun başında, okunur adımlar ve "Edit
            // approval chain" (ortak bulamıyordu; ADR 0073 §3)
            <ApprovalChainPanel
              detail={detail}
              office={office}
              canEdit={canEditDocument(writeContext, detail, permissions)}
              onSaved={() => {
                setNotice({ tone: 'success', text: t('documents.chain.saved') });
                void loadDetail(detail.id);
              }}
            />
          ) : null}
          {detail ? (
            <DocumentSidebar
              section={section}
              scope={scope}
              detail={detail}
              revision={revision}
              permissions={permissions}
              canOutput={canOutput}
              outputHint={outputHint}
              openBlocked={missing !== null}
              offsetMin={offsetMin}
              onOpen={() => {
                // Tam ekran önizleyici Form View panelindedir; gizli panelde görünmez
                setTab('view');
                viewer.current?.fullscreen();
              }}
              onDownload={downloadMain}
              onPrint={() => void print()}
              formActions={formActions}
              onFavorite={readOnly ? undefined : () => void toggleFavorite()}
              onDownloadRelated={downloadRelated}
            />
          ) : null}
          {detail ? null : loading ? (
            <>
              <Skeleton height={420} radius="md" />
              <Skeleton height={220} radius="md" />
            </>
          ) : null}
        </aside>
      </div>

      {/* Dar ekranda (< 768 px) indir ve yazdır alttaki sabit çubukta (README "Responsive") */}
      {permissions.export && detail ? (
        <div className={classes.bottomBar}>
          <Button
            variant="default"
            leftSection={<IconDownload size={16} />}
            disabled={!canOutput}
            aria-describedby={outputHint ? outputHintId : undefined}
            onClick={downloadMain}
          >
            {t('documents.head.download')}
          </Button>
          <Button
            leftSection={<IconPrinter size={16} />}
            disabled={!canOutput}
            aria-describedby={outputHint ? outputHintId : undefined}
            onClick={() => void print()}
          >
            {t('documents.head.print')}
          </Button>
          {outputHint ? (
            <span id={outputHintId} className={classes.srOnly}>
              {outputHint}
            </span>
          ) : null}
        </div>
      ) : null}

      <Modal
        opened={confirmDownload !== null}
        onClose={() => setConfirmDownload(null)}
        title={t('documents.head.download')}
        closeButtonProps={{ 'aria-label': t('documents.screen.dismiss') }}
        centered
      >
        <Text size="sm">{t('documents.viewer.notCurrent')}</Text>
        <Group justify="flex-end" mt="lg">
          <Button variant="default" onClick={() => setConfirmDownload(null)}>
            {t('documents.acknowledge.cancel')}
          </Button>
          <Button
            leftSection={<IconDownload size={16} />}
            onClick={() => {
              const file = confirmDownload;
              setConfirmDownload(null);
              if (file) void source.downloadFile(file).catch(fail);
            }}
          >
            {t('documents.head.download')}
          </Button>
        </Group>
      </Modal>
      {/* Pencereler bağlı kalır, açılıp kapanır: Mantine odağı açan düğmeye geri verir (S5) */}
      {detail && revision ? (
        <AcknowledgeDialog
          opened={ackTarget !== null}
          kind={(ackTarget ?? lastAck)?.kind ?? detail.acknowledgementDue?.kind ?? 'adopted'}
          detail={detail}
          revision={
            (ackTarget ?? lastAck)?.revision ?? changesRevision(detail, revision) ?? revision
          }
          onClose={() => setAckTarget(null)}
          onConfirm={acknowledge}
        />
      ) : null}
      {detail && revision ? (
        <ReadDialog
          opened={readTarget !== null}
          detail={detail}
          revision={readTarget ?? lastRead ?? revision}
          onClose={() => setReadTarget(null)}
          onConfirm={confirmRead}
        />
      ) : null}
      {workflow && detail && actionRevision ? (
        <RevisionActionDialog
          action={pendingAction?.action ?? null}
          detail={detail}
          revision={actionRevision}
          onClose={() => setPendingAction(null)}
          onConfirm={act}
        />
      ) : null}
      {office?.removeDocument && detail && removing ? (
        <RemoveDocumentDialog
          title={documentHeading(detail)}
          onCancel={() => setRemoving(false)}
          onConfirm={async () => {
            try {
              await office.removeDocument?.(detail.id, detail.rowVersion);
              setRemoving(false);
              setNotice({ tone: 'success', text: t('documents.remove.done') });
              go(null);
              void loadTree();
            } catch (error) {
              setRemoving(false);
              fail(error);
            }
          }}
        />
      ) : null}
      {office || shipLibrary ? (
        <RevisionDrawer
          opened={drawer !== null}
          mode={drawer ?? 'new'}
          section={section}
          tree={tree}
          detail={detail}
          office={office}
          ship={shipLibrary}
          onClose={() => setDrawer(null)}
          onSaved={(documentId, saved) => {
            setDrawer(null);
            setNotice({
              tone: 'success',
              text: t('documents.drawer.saved', {
                revision: revisionBare(saved.revisionNo),
              }),
            });
            void loadTree();
            if (documentId === selectedId) void loadDetail(documentId);
            go(documentId);
          }}
          onPartialFailure={(documentId) => {
            // Bir adım yazıldı (doküman, revizyon ya da kapsam): ekran bayat kalmaz (WF-2)
            void loadTree();
            if (documentId === selectedId) void loadDetail(documentId);
          }}
        />
      ) : null}
      {formActions?.dialogs}
      {printing ? (
        <div className={classes.printing} role="status">
          <IconPrinter size={16} aria-hidden />
          {t('documents.viewer.preparingPrint')}
        </div>
      ) : null}
    </div>
  );
}

/**
 * "Remove document" onayı (ADR 0142 §5): doküman soft delete olur, revizyonları ve olayları
 * kalır. Yürürlükte revizyonu olan önce geri çekilir; kullanılmış doküman kaldırılmaz.
 */
export function RemoveDocumentDialog({
  title,
  onCancel,
  onConfirm,
}: {
  title: string;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  return (
    <Modal opened onClose={onCancel} title={t('documents.remove.title', { title })} centered>
      <Text fz="sm">{t('documents.remove.text')}</Text>
      <Group justify="flex-end" mt="md">
        <Button variant="default" onClick={onCancel} disabled={busy}>
          {t('documents.remove.cancel')}
        </Button>
        <Button
          color="red"
          loading={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await onConfirm();
            } finally {
              setBusy(false);
            }
          }}
        >
          {t('documents.remove.confirm')}
        </Button>
      </Group>
    </Modal>
  );
}
